import { devLog, devError } from '../utils/devLogger';
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  Timestamp,
  where
} from 'firebase/firestore';
import { db } from '../firebase';
import realTimeProgressService from './realTimeProgressService';
import attendanceService from './attendanceService';
import documentDeadlineService from './documentDeadlineService';

const CHUNK_SIZE = 10;

const chunkArray = (arr, size) => {
  const chunks = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
};

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (value?.toDate) return value.toDate();
  if (value instanceof Timestamp) return value.toDate();
  return new Date(value);
};

class LearnerDataService {
  async getLearnerSnapshot(userId, userProfile) {
    if (!userId) {
      throw new Error('LearnerDataService: userId is required');
    }

    return this.getFirestoreSnapshot(userId, userProfile);
  }

  async getFirestoreSnapshot(userId, userProfile) {
    try {
      const [modules, progressMap, assignments, attendanceResult, pendingSessionsResult, documentStatus, upcomingSessions] =
        await Promise.all([
          this.fetchModulesForLearner(userProfile),
          realTimeProgressService.getUserProgress(userId),
          this.fetchAssignmentsForLearner(userId, userProfile),
          attendanceService.getLearnerAttendanceSummary(userId, userProfile?.cohortId || null),
          attendanceService.getPendingSessionsForLearner(userId, userProfile?.cohortId || null),
          documentDeadlineService.getUserDocumentStatus(userId),
          this.fetchUpcomingSessions(userProfile)
        ]);

      const achievements = this.extractAchievements(progressMap);
      const stats = this.buildDashboardStats({
        modules,
        progressMap,
        assignments,
        attendanceSummary: attendanceResult?.summary || null,
        achievements,
        upcomingSessions
      });

      return {
        modules,
        progress: progressMap,
        assignments,
        attendance: attendanceResult?.records || [],
        attendanceSummary: attendanceResult?.summary || null,
        pendingSessions: pendingSessionsResult?.sessions || [],
        documents: documentStatus?.data || null,
        achievements,
        upcomingSessions,
        stats
      };
    } catch (error) {
      devError('LearnerDataService: Failed to load snapshot', error);
      throw error;
    }
  }

  async fetchModulesForLearner(userProfile) {
    const modulesCollection = collection(db, 'modules');
    const modules = [];
    const cohortIds = Array.from(new Set([
      userProfile?.cohortId,
      ...(Array.isArray(userProfile?.cohortIds) ? userProfile.cohortIds : []),
      ...(Array.isArray(userProfile?.assignedCohorts) ? userProfile.assignedCohorts : [])
    ].filter(Boolean)));

    const addModulesFromSnapshot = (snapshot) => {
      snapshot.docs.forEach((moduleDoc) => {
        const data = moduleDoc.data();
        modules.push({
          ...data,
          id: moduleDoc.id,
          moduleCode: data.id || null,
          createdAt: toDate(data.createdAt),
          updatedAt: toDate(data.updatedAt),
          startDate: toDate(data.startDate),
          endDate: toDate(data.endDate)
        });
      });
    };

    for (const cohortId of cohortIds) {
      try {
        const cohortQuery = query(
          modulesCollection,
          where('cohortIds', 'array-contains', cohortId)
        );
        const cohortSnapshot = await getDocs(cohortQuery);
        addModulesFromSnapshot(cohortSnapshot);
      } catch (error) {
        devLog('LearnerDataService: cohortIds query failed', error);
      }

      try {
        const legacyQuery = query(
          modulesCollection,
          where('cohortId', '==', cohortId)
        );
        const legacySnapshot = await getDocs(legacyQuery);
        addModulesFromSnapshot(legacySnapshot);
      } catch (error) {
        devLog('LearnerDataService: cohortId query failed', error);
      }
    }

    if (userProfile?.programId) {
      try {
        const programQuery = query(
          modulesCollection,
          where('programId', '==', userProfile.programId)
        );
        const programSnapshot = await getDocs(programQuery);
        programSnapshot.docs.forEach((moduleDoc) => {
          const data = moduleDoc.data();
          const hasCohortAssignments = (Array.isArray(data.cohortIds) && data.cohortIds.length > 0) || data.cohortId;
          if (hasCohortAssignments) return;
          modules.push({
            ...data,
            id: moduleDoc.id,
            moduleCode: data.id || null,
            createdAt: toDate(data.createdAt),
            updatedAt: toDate(data.updatedAt),
            startDate: toDate(data.startDate),
            endDate: toDate(data.endDate)
          });
        });
      } catch (error) {
        devLog('LearnerDataService: programId query failed', error);
      }
    }

    const unique = new Map();
    modules
      .filter((module) => module.status !== 'archived')
      .forEach((module) => {
        unique.set(module.id, module);
      });

    return Array.from(unique.values()).sort((a, b) => {
      const aDate = a.startDate || a.createdAt || new Date(0);
      const bDate = b.startDate || b.createdAt || new Date(0);
      return aDate - bDate;
    });
  }

  async fetchAssignmentsForLearner(userId, userProfile) {
    const assignmentsCollection = collection(db, 'assignments');
    const moduleIds = await this.fetchModuleIdsForLearner(userProfile);

    const assignments = [];

    const moduleChunks = chunkArray(moduleIds, CHUNK_SIZE);

    // Fetch assignments per chunk to stay within Firestore limits
    // eslint-disable-next-line no-restricted-syntax
    for (const chunk of moduleChunks) {
      const chunkQuery = query(assignmentsCollection, where('moduleId', 'in', chunk));
      // eslint-disable-next-line no-await-in-loop
      const chunkSnapshot = await getDocs(chunkQuery);
      chunkSnapshot.docs.forEach((assignmentDoc) => {
        const data = assignmentDoc.data();
        const userSubmission = data.submissions?.[userId] || null;
        assignments.push({
          id: assignmentDoc.id,
          ...data,
          userSubmission,
          dueDate: toDate(data.dueDate),
          assignedDate: toDate(data.assignedDate),
          submissionDeadline: toDate(data.submissionDeadline)
        });
      });
    }

    // Include assignments directly targeted at learner or cohort
    try {
      const learnerTargetedQuery = query(
        assignmentsCollection,
        where('assignedLearners', 'array-contains', userId)
      );
      const learnerTargetedSnapshot = await getDocs(learnerTargetedQuery);
      learnerTargetedSnapshot.docs.forEach((assignmentDoc) => {
        const data = assignmentDoc.data();
        const userSubmission = data.submissions?.[userId] || null;
        assignments.push({
          id: assignmentDoc.id,
          ...data,
          userSubmission,
          dueDate: toDate(data.dueDate),
          assignedDate: toDate(data.assignedDate),
          submissionDeadline: toDate(data.submissionDeadline)
        });
      });
    } catch (error) {
      devLog('LearnerDataService: assignedLearners query failed', error);
    }

    const cohortIds = Array.from(new Set([
      userProfile?.cohortId,
      ...(Array.isArray(userProfile?.cohortIds) ? userProfile.cohortIds : []),
      ...(Array.isArray(userProfile?.assignedCohorts) ? userProfile.assignedCohorts : [])
    ].filter(Boolean)));

    for (const cohortId of cohortIds) {
      try {
        const cohortQuery = query(
          assignmentsCollection,
          where('cohortId', '==', cohortId)
        );
        const cohortSnapshot = await getDocs(cohortQuery);
        cohortSnapshot.docs.forEach((assignmentDoc) => {
          const data = assignmentDoc.data();
          const userSubmission = data.submissions?.[userId] || null;
          assignments.push({
            id: assignmentDoc.id,
            ...data,
            userSubmission,
            dueDate: toDate(data.dueDate),
            assignedDate: toDate(data.assignedDate),
            submissionDeadline: toDate(data.submissionDeadline)
          });
        });
      } catch (error) {
        devLog('LearnerDataService: cohort assignments query failed', error);
      }
    }

    const unique = new Map();
    assignments.forEach((assignment) => {
      unique.set(assignment.id, assignment);
    });

    return Array.from(unique.values()).sort((a, b) => {
      const aDate = a.dueDate || a.assignedDate || new Date(0);
      const bDate = b.dueDate || b.assignedDate || new Date(0);
      return aDate - bDate;
    });
  }

  async fetchModuleIdsForLearner(userProfile) {
    const modules = await this.fetchModulesForLearner(userProfile);
    return modules.map((module) => module.id);
  }

  async fetchUpcomingSessions(userProfile) {
    if (!userProfile?.cohortId) {
      return [];
    }

    try {
      const now = new Date();
      const sessionsQuery = query(
        collection(db, 'sessions'),
        where('cohortId', '==', userProfile.cohortId),
        where('scheduledDate', '>=', Timestamp.fromDate(now)),
        orderBy('scheduledDate', 'asc'),
        limit(5)
      );

      const snapshot = await getDocs(sessionsQuery);
      return snapshot.docs.map((sessionDoc) => {
        const data = sessionDoc.data();
        return {
          id: sessionDoc.id,
          ...data,
          scheduledDate: toDate(data.scheduledDate),
          actualDate: toDate(data.actualDate)
        };
      });
    } catch (error) {
      devLog('LearnerDataService: Failed to load upcoming sessions', error);
      return [];
    }
  }

  extractAchievements(progressMap) {
    const achievements = [];
    Object.values(progressMap || {}).forEach((moduleProgress) => {
      if (Array.isArray(moduleProgress?.achievements)) {
        achievements.push(...moduleProgress.achievements);
      }
    });
    return Array.from(new Set(achievements));
  }

  buildDashboardStats({ modules, progressMap, assignments, attendanceSummary, achievements, upcomingSessions }) {
    const moduleProgressList = Object.values(progressMap || {});
    const totalModules = modules.length || moduleProgressList.length;

    const totalProgress = moduleProgressList.reduce((sum, moduleProgress) => sum + (moduleProgress.progress || 0), 0);
    const overallProgress = totalModules > 0 ? Math.round(totalProgress / totalModules) : 0;
    const modulesCompleted = moduleProgressList.filter((moduleProgress) => (moduleProgress.progress || 0) >= 100).length;

    const assignmentsWithDueDates = assignments.filter((assignment) => assignment.dueDate);
    const now = new Date();
    const overdueAssignments = assignmentsWithDueDates.filter((assignment) => {
      if (assignment.userSubmission?.status === 'completed' || assignment.userSubmission?.status === 'graded') {
        return false;
      }
      return assignment.dueDate < now;
    }).length;

    const dueSoonAssignments = assignmentsWithDueDates.filter((assignment) => {
      if (assignment.userSubmission?.status === 'completed' || assignment.userSubmission?.status === 'graded') {
        return false;
      }
      const difference = assignment.dueDate - now;
      const threeDays = 3 * 24 * 60 * 60 * 1000;
      return difference >= 0 && difference <= threeDays;
    }).length;

    const attendanceRate = attendanceSummary?.attendanceRate || 0;

    const nextSession = upcomingSessions?.[0] || null;

    return {
      overallProgress,
      modulesCompleted,
      totalModules,
      achievementsUnlocked: achievements.length,
      assignmentsTotal: assignments.length,
      assignmentsOverdue: overdueAssignments,
      assignmentsDueSoon: dueSoonAssignments,
      attendanceRate,
      nextSession
    };
  }

}

const learnerDataService = new LearnerDataService();

export default learnerDataService;
